// Sprite factory. Detailed buildings and resource clusters use atelier.ts;
// small characters use character grids. Both bake to nearest-neighbor textures.
// The factory interface keeps visual changes independent of simulation state.

import { Container, Graphics, Sprite } from 'pixi.js';
import { TILE_W, TILE_H } from './iso';
import { bakeGrid, bakeGround, shade } from './pixelart';
import type { ResourceId } from '@data/resources';
import { type BuildingId } from '@data/buildings';
import { visualEraIndex as eraIndex, ageVisual, type WorkerVehicle } from '@data/visual-ages';
import { actorArt } from './actors';
import { buildingArt, resourceArt } from './atelier';

export { shade };
export { eraIndex }; // re-exported for the render layer (world.ts imports it here)

const PX = 2; // pixel scale for object sprites

// ---------------------------------------------------------------------------
// Environment accent palette. Building and actor silhouettes follow the exact
// current age, including already-placed structures during a live transition.
// ---------------------------------------------------------------------------

const ERA_TINTS = [
  0xffe2bc, // 0 steppe / ancient: warm earthen
  0xefeadd, // 1 early kingdoms: pale stone
  0xffd98c, // 2 imperial: gilded
  0xd6e6fb, // 3 republic / modern: cool, clean
  0xc4c0ff, // 4 space age: violet steel
];
export function eraTint(age: number): number {
  return ERA_TINTS[eraIndex(age)];
}

// Era ground bases: the steppe starts as a dry gold-green bozkır, turns lush
// under the kingdoms, warms to imperial olive, cools in the modern age, and
// ends as violet regolith among the stars. Roads darken to pavement late.
export const ERA_GROUND = [0x92965d, 0x738b53, 0x898753, 0x747763, 0x565a70];
export const ERA_PATH = [0xb09b70, 0xa99a77, 0xb0a285, 0x858582, 0x6c7087];

// ---------------------------------------------------------------------------
// Resource node art
// ---------------------------------------------------------------------------

export interface NodeSprite {
  root: Container;
  shape: Container; // scaled / faded by HP
}

export function makeNodeSprite(resource: ResourceId, age = 0): NodeSprite {
  const root = new Container();

  // soft ground shadow under the node
  const shadow = new Graphics();
  shadow.ellipse(0, 0, TILE_W * 0.26, TILE_H * 0.22).fill({ color: 0x000000, alpha: 0.22 });
  root.addChild(shadow);

  const tex = resourceArt(resource, age);
  const spr = new Sprite(tex);
  spr.anchor.set(0.5, 48 / 56);
  spr.y = TILE_H * 0.28; // base sits a touch below the tile center
  root.addChild(spr);

  return { root, shape: spr };
}

// ---------------------------------------------------------------------------
// Buildings: distinct pixel sprites per type, with residential variety.
// Roof palette per type is baked into the grid; tier adds accents + scale.
// ---------------------------------------------------------------------------

/** The art owns lighting and tier details; never mirror or stretch the baked pixels. */
export function makeBuildingSprite(id: BuildingId, tier = 0, age = 0, variant = 0): Container {
  const root = new Container();
  const spr = new Sprite(buildingArt(id, age, tier, variant));
  spr.anchor.set(0.5, 48 / 56);
  spr.y = TILE_H * 0.34;
  root.addChild(spr);
  return root;
}

export function makeTownCenter(age = 0, tier = 0): Container {
  const root = new Container();
  const spr = new Sprite(buildingArt('keep', age, tier));
  spr.anchor.set(0.5, 48 / 56);
  spr.y = TILE_H * 0.34;
  root.addChild(spr);
  return root;
}

// ---------------------------------------------------------------------------
// Ground tiles
// ---------------------------------------------------------------------------

export function makeGround(base: number, seed: number): Sprite {
  const tex = bakeGround(`ground:${base}:${seed % 6}`, TILE_W, TILE_H, base, seed);
  const spr = new Sprite(tex);
  spr.anchor.set(0.5, 0.5);
  return spr;
}

// ---------------------------------------------------------------------------
// Ground decor: tiny scatter props that break the tile monotony. Flowers and
// tufts on living ground; pebbles everywhere; glow crystals on the regolith.
// ---------------------------------------------------------------------------

// Decor bakes at the world's 3px density (same as buildings/nodes) so the
// ground scatter sits on the same pixel grid instead of a finer, mismatched one.
const DECOR_GRIDS: Record<string, string[]> = {
  flower: ['f.r.', '.g..', 'g.g.'],
  tuft: ['g.G.g', '.gGg.'],
  pebble: ['.Sz.', 'z.S.'],
  crystal: ['.q..', 'qq.q', '.q..'],
};

export type DecorKind = keyof typeof DECOR_GRIDS;

export function makeDecorSprite(kind: DecorKind): Sprite {
  const spr = new Sprite(bakeGrid(`decor:${kind}`, DECOR_GRIDS[kind], PX));
  spr.anchor.set(0.5, 0.8);
  return spr;
}

// ---------------------------------------------------------------------------
// Bozkurt: the pale steppe wolf, the game's silent mascot. It trots across an
// outer lane after a held wave, pauses to howl at the eternal sky, and leaves.
// ---------------------------------------------------------------------------

const WOLF = [
  '..s.........',
  '.sS......ss.',
  '..sS....sSs.',
  '...sSssSSSf.',
  '...SSSSSSSz.',
  '...SSSSSSs..',
  '...S..S.S...',
  '...z..z.z...',
];

export function makeWolfSprite(): Sprite {
  const spr = new Sprite(bakeGrid('critter:wolf', WOLF, 2));
  spr.anchor.set(0.5, 0.9);
  return spr;
}

// ---------------------------------------------------------------------------
// Combat units (marching enemies + settlement defenders)
// ---------------------------------------------------------------------------

export type UnitKind = 'grunt' | 'brute' | 'boss' | 'defender';
export type { WorkerVehicle };
export function makeUnitSprite(kind: UnitKind, age = 0): Sprite {
  const spr = new Sprite(actorArt(kind, age));
  spr.anchor.set(0.5, 26 / 28);
  return spr;
}
export function workerVehicle(age: number): WorkerVehicle { return ageVisual(age).transport; }
export function makeWorkerSprite(_vehicle: WorkerVehicle, age = 0): Sprite {
  const spr = new Sprite(actorArt('worker', age));
  spr.anchor.set(0.5, 26 / 28);
  return spr;
}

// ---------------------------------------------------------------------------
// Cursor damage circle (kept as Graphics; it is a UI affordance, not art)
// ---------------------------------------------------------------------------

export function makeCursorCircle(): Graphics {
  return new Graphics();
}

export function drawCursorCircle(g: Graphics, radius: number, hot: boolean): void {
  // Iso ground oval (height = radius/2) so the harvest area sits on the tiles
  // instead of floating as a tall circle.
  const ry = radius * 0.5;
  const color = hot ? 0xffd24a : 0x8fc7ff;
  g.clear();
  // The hot/cold difference used to be a thin blue-vs-gold line at the same
  // weight, which on a busy green field read as no difference at all: a player
  // parked on empty grass got no signal that they were working nothing. Hot is
  // now clearly brighter and heavier than cold.
  g.ellipse(0, 0, radius, ry).fill({ color, alpha: hot ? 0.16 : 0.05 });
  g.ellipse(0, 0, radius, ry).stroke({ width: hot ? 3 : 1.5, color, alpha: hot ? 0.9 : 0.35 });
  g.circle(0, 0, 2.5).fill({ color: 0xffffff, alpha: hot ? 0.9 : 0.5 });
}
